import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { FormEvent, useState } from "react";

import { useDialogFocus } from "../../app/useDialogFocus";
import { getProjectRepository } from "../../data/projectRepository";
import "./projects.css";
import type { ProjectSummary } from "./types";

type Action =
  | { kind: "create" }
  | { kind: "copy"; project: ProjectSummary }
  | { kind: "rename"; project: ProjectSummary }
  | { kind: "delete"; project: ProjectSummary }
  | null;

const guideSteps: Array<{ title: string; text: string; icon: React.JSX.Element }> = [
  {
    title: "创建课表",
    text: "从空白开始，或复制已有课表复用基础条件和排课结果。",
    icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false">
        <path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z" />
        <path d="M14 3v5h5M12 12v6M9 15h6" />
      </svg>
    )
  },
  {
    title: "准备基础信息",
    text: "填写或批量导入课程、功能教室、教师和班级，再设置上下午节次和不排课时间。也可以先用模拟数据快速体验。",
    icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false">
        <path d="M8 6h13M8 12h13M8 18h13" />
        <path d="M3.5 6h.01M3.5 12h.01M3.5 18h.01" />
      </svg>
    )
  },
  {
    title: "准备任课计划",
    text: "先按年级填写通用需求，再补充班级需求、教师任课范围和功能教室安排；检查后生成详细计划。",
    icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false">
        <rect x="5" y="4" width="14" height="17" rx="2" />
        <path d="M9 4V3h6v1M9 11l2 2 4-4" />
      </svg>
    )
  },
  {
    title: "生成课表",
    text: "检查详细计划后先预检全部硬条件；预检通过后直接生成，发现确定矛盾时按量化建议修改并重新预检。想尝试另一套排法时先复制课表。",
    icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false">
        <rect x="3" y="4" width="18" height="17" rx="2" />
        <path d="M3 9h18M9 4v17M15 9v12" />
      </svg>
    )
  },
  {
    title: "微调并导出",
    text: "按班级或教师查看课表，通过拖拽、移动或交换调整，确认后可单个或批量导出 Excel。",
    icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false">
        <path d="M12 3v12M7.5 10.5 12 15l4.5-4.5" />
        <path d="M4 17v2a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-2" />
      </svg>
    )
  }
];

function GuideDialog({ onClose }: { onClose: () => void }): React.JSX.Element {
  const dialogRef = useDialogFocus(onClose);
  return (
    <div className="modal-backdrop" role="presentation">
      <section
        ref={dialogRef}
        className="modal-card guide-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="guide-title"
      >
        <div className="guide-dialog-heading">
          <h2 id="guide-title">5 步完成一份课表</h2>
          <button
            className="dialog-close"
            type="button"
            aria-label="关闭使用说明"
            onClick={onClose}
          >
            ×
          </button>
        </div>
        <ol className="guide-steps">
          {guideSteps.map((step, index) => (
            <li key={step.title}>
              <div className="guide-step-top">
                <span className="guide-step-num">{index + 1}</span>
                <span className="guide-step-icon">{step.icon}</span>
              </div>
              <h3>{step.title}</h3>
              <p>{step.text}</p>
            </li>
          ))}
        </ol>
        <p className="guide-tip">
          手动调整可以覆盖不排课时间，但仍会保证班级、主教、助教和功能教室不冲突。
        </p>
        <p className="guide-tip">
          课表只保存在当前浏览器中；清除本站数据会导致课表丢失，也不会自动同步到其他设备。排课和 Excel
          导出时，当前项目会通过 HTTPS 临时发送给计算服务，但服务器不保存项目。
        </p>
      </section>
    </div>
  );
}

function formatDate(value: string): string {
  return new Intl.DateTimeFormat("zh-CN", {
    dateStyle: "medium",
    timeStyle: "short"
  }).format(new Date(value));
}

export function ProjectsPage(): React.JSX.Element {
  const queryClient = useQueryClient();
  const [searchDraft, setSearchDraft] = useState("");
  const [query, setQuery] = useState("");
  const [action, setAction] = useState<Action>(null);
  const [guideOpen, setGuideOpen] = useState(false);
  const [name, setName] = useState("");
  const [message, setMessage] = useState("");
  const projects = useQuery({
    queryKey: ["projects", query],
    queryFn: async () => (await getProjectRepository()).list(query)
  });

  const mutation = useMutation({
    mutationFn: async () => {
      if (!action) return;
      const repository = await getProjectRepository();
      if (action.kind === "create") {
        return repository.create(name);
      }
      if (action.kind === "copy") {
        return repository.copy(action.project.id, name);
      }
      if (action.kind === "rename") {
        return repository.rename(
          action.project.id,
          name,
          action.project.condition_revision
        );
      }
      return repository.delete(action.project.id);
    },
    onSuccess: async (result) => {
      await queryClient.invalidateQueries({ queryKey: ["projects"] });
      const wasCreate = action?.kind === "create";
      setAction(null);
      setName("");
      setMessage("");
      if (wasCreate && result) window.location.assign(`/projects/${result.id}`);
    },
    onError: (error) => {
      setMessage(error instanceof Error ? error.message : "操作失败，请稍后重试");
    }
  });
  const dialogRef = useDialogFocus(
    () => setAction(null),
    Boolean(action) && !mutation.isPending,
    Boolean(action)
  );

  function openAction(next: Exclude<Action, null>): void {
    setAction(next);
    setMessage("");
    if (next.kind === "create") setName("");
    if (next.kind === "copy") setName(`${next.project.name}-副本`);
    if (next.kind === "rename") setName(next.project.name);
  }

  function submit(event: FormEvent): void {
    event.preventDefault();
    mutation.mutate();
  }

  const items = projects.data?.items ?? [];
  // 最近项目只从现有列表的 updated_at 推导；搜索时让位给纯结果列表。
  const recent = query
    ? null
    : items.reduce<ProjectSummary | null>(
        (latest, project) =>
          !latest || new Date(project.updated_at) > new Date(latest.updated_at)
            ? project
            : latest,
        null
      );
  const ready = !projects.isLoading && !projects.isError;
  return (
    <main className="page-shell projects-page">
      <header className="page-header">
        <div>
          <h1>创建或继续一份课表</h1>
        </div>
        <div className="header-actions hero-actions">
          <button className="guide-button" type="button" onClick={() => setGuideOpen(true)}>
            <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false">
              <circle cx="12" cy="12" r="9" />
              <path d="M9.2 9a2.8 2.8 0 0 1 5.5.7c0 1.8-2.7 2.3-2.7 3.8" />
              <path d="M12 17h.01" />
            </svg>
            5 步快速上手
          </button>
        </div>
      </header>

      {projects.isLoading ? <p className="state-message">正在加载课表项目…</p> : null}
      {projects.isError ? <p className="error-message">项目列表加载失败，请刷新重试。</p> : null}

      {ready ? (
        <section
          className={`continue-section${recent ? "" : " no-recent"}`}
          aria-label="开始工作"
        >
          {recent ? (
            <div className="continue-card">
              <div className="continue-card-main">
                <p className="continue-kicker">继续最近课表</p>
                <h2 className="continue-title">{recent.name}</h2>
                <p className="continue-meta">
                  更新于 {formatDate(recent.updated_at)} ·{" "}
                  {recent.conditions_locked_at ? "条件已锁定" : "可编辑条件"}
                </p>
              </div>
              <a className="solid-button continue-action" href={`/projects/${recent.id}`}>
                继续
              </a>
            </div>
          ) : null}
          <div className="new-project-card">
            <p className="new-project-title">开始一份新课表</p>
            <button
              className="solid-button"
              type="button"
              onClick={() => openAction({ kind: "create" })}
            >
              新建课表
            </button>
          </div>
        </section>
      ) : null}

      {ready ? (
        <section className="project-library-section" aria-labelledby="library-title">
          <div className="library-head">
            <div>
              <p className="section-kicker">全部项目</p>
              <h2 id="library-title">课表项目库</h2>
            </div>
            <form
              className="search-bar library-search"
              onSubmit={(event) => {
                event.preventDefault();
                setQuery(searchDraft.trim());
              }}
            >
              <label htmlFor="project-search">搜索课表</label>
              <div className="search-controls">
                <input
                  id="project-search"
                  value={searchDraft}
                  onChange={(event) => setSearchDraft(event.target.value)}
                  placeholder="输入课表名称"
                />
                <button className="secondary-button" type="submit">
                  搜索
                </button>
                {query ? (
                  <button
                    className="text-button"
                    type="button"
                    onClick={() => {
                      setSearchDraft("");
                      setQuery("");
                    }}
                  >
                    清除
                  </button>
                ) : null}
              </div>
            </form>
          </div>

          {items.length === 0 ? (
            <div className="empty-card compact">
              <div className="empty-icon" aria-hidden="true">课</div>
              <h2>{query ? "没有匹配的课表" : "还没有课表项目"}</h2>
              {query ? <p>换个关键词试试。</p> : null}
            </div>
          ) : null}

          {items.length ? (
            <div className="project-library" role="table" aria-label="课表项目库列表">
              <div role="rowgroup">
                {items.map((project) => (
                  <div className="project-row" role="row" key={project.id}>
                    <div className="project-row-main" role="cell">
                      <a className="project-link" href={`/projects/${project.id}`}>
                        {project.name}
                      </a>
                      <span className="project-row-meta">
                        更新于 {formatDate(project.updated_at)}
                      </span>
                    </div>
                    <div className="project-row-status" role="cell">
                      <span className={`pill${project.conditions_locked_at ? " warn" : ""}`}>
                        {project.conditions_locked_at ? "条件已锁定" : "可编辑条件"}
                      </span>
                    </div>
                    <div className="project-row-actions" role="cell">
                      <a className="mini-button" href={`/projects/${project.id}`}>查看</a>
                      <button className="mini-button" type="button" onClick={() => openAction({ kind: "copy", project })}>复制</button>
                      <button className="mini-button" type="button" onClick={() => openAction({ kind: "rename", project })}>改名</button>
                      <button className="mini-button danger-text" type="button" onClick={() => openAction({ kind: "delete", project })}>删除</button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : null}
        </section>
      ) : null}

      {guideOpen ? <GuideDialog onClose={() => setGuideOpen(false)} /> : null}

      {action ? (
        <div className="modal-backdrop" role="presentation">
          <section ref={dialogRef} className="modal-card" role="dialog" aria-modal="true" aria-labelledby="modal-title">
            <form onSubmit={submit}>
              <h2 id="modal-title">
                {action.kind === "create" ? "创建空白课表" : null}
                {action.kind === "copy" ? "复制课表" : null}
                {action.kind === "rename" ? "修改课表名称" : null}
                {action.kind === "delete" ? "删除课表" : null}
              </h2>
              {action.kind === "delete" ? (
                <p>将删除“{action.project.name}”及其中全部条件、计划和课表。此操作无法撤销。</p>
              ) : (
                <label className="field">
                  <span>课表名称</span>
                  <input value={name} onChange={(event) => setName(event.target.value)} />
                </label>
              )}
              {message ? <p className="error-message" role="alert">{message}</p> : null}
              <div className="modal-actions">
                <button className="secondary-button" type="button" onClick={() => setAction(null)}>取消</button>
                <button
                  className={`primary-button ${action.kind === "delete" ? "danger-button" : ""}`}
                  type="submit"
                  disabled={mutation.isPending}
                >
                  {mutation.isPending ? "正在处理…" : action.kind === "delete" ? "确认删除" : "确认"}
                </button>
              </div>
            </form>
          </section>
        </div>
      ) : null}
    </main>
  );
}
